// src/api/hooks/useIngestion.js — v8.9 INGESTION FIX
//
// ИСПРАВЛЕНО — причина чёрного экрана /ingestion:
//
// 1. useCallback(fn, [parseCSV]) — ссылка на несуществующую переменную parseCSV
//    После патча polish parseCSV была удалена и заменена на module-level smartParseCSV.
//    Но dependency array не был обновлён → ReferenceError при инициализации модуля
//    → React error boundary ломал весь tree → чёрный экран.
//    ФИКС: убрать parseCSV из deps (smartParseCSV — чистая функция, deps не нужны).
//
// 2. Невалидный regex в smartParseCSV: /^["\\'']|["\\'']$/g
//    В JS-строке \'  = ' (одиночная кавычка, нет нужды экранировать в строке).
//    Но в character class внутри RegExp /[\\'']/ — backslash перед ' избыточен,
//    и при определённых движках может создавать unexpected token.
//    ФИКС: /^["']|["']$/g — чистый regexp без избыточных backslash.

import { useState, useEffect, useCallback } from "react";
import { workerClient, BASE_URL } from "../workerClient";

// ─────────────────────────────────────────────────────────────
// smartParseCSV — авто-детекция колонок
// Поддерживает: Keitaro, Binom, Excel, Google Sheets, произвольные CSV
// Разделители: , ; tab (авто-детект)
// Форматы дат: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, unix timestamp
// Числа: US "1,234.56", EU "1.234,56", с символом валюты
// ─────────────────────────────────────────────────────────────

const DATE_PATTERNS    = ["date","day","dt","reportdate","report_date","createdat","created_at","timestamp","time"];
const SPEND_PATTERNS   = ["spend","cost","adspend","ad_spend","trafficcost","traffic_cost","amountspent","amount_spent","spendamount","totalcost","expenses","budget"];
const REVENUE_PATTERNS = ["revenue","rev","income","payout","profitbeforecosts","profit_before_costs","totalrevenue","earnings","gross"];

function normH(h) {
  return String(h).toLowerCase().trim().replace(/[\s\-_]+/g, "");
}

function findCol(headers, patterns) {
  const norm = headers.map(h => ({ orig: h, n: normH(h) }));
  for (const p of patterns) {
    const f = norm.find(h => h.n === p);
    if (f) return f.orig;
  }
  for (const p of patterns) {
    const f = norm.find(h => h.n.includes(p));
    if (f) return f.orig;
  }
  return null;
}

function parseNum(raw) {
  if (raw == null || raw === "") return 0;
  let s = String(raw).trim().replace(/[€$£¥\s'"]/g, "");
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(,\d{3})*(\.\d+)?$/.test(s)) s = s.replace(/,/g, "");
  else if (/^\d+,\d{1,2}$/.test(s)) s = s.replace(",", ".");
  return parseFloat(s) || 0;
}

function parseDateStr(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/^\d{10}$/.test(s)) return new Date(Number(s) * 1000).toISOString().slice(0, 10);
  if (/^\d{13}$/.test(s)) return new Date(Number(s)).toISOString().slice(0, 10);
  const iso = s.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})/);
  if (iso) {
    const d = new Date(`${iso[1]}-${iso[2]}-${iso[3]}`);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  }
  const eu = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (eu) {
    const p1 = +eu[1], p2 = +eu[2], yr = eu[3];
    const day = p1 > 12 ? p1 : p1;
    const mon = p1 > 12 ? p2 : p2;
    // DD/MM: если p1 > 12 → точно день; иначе евро-приоритет DD/MM
    const d = p1 > 12
      ? new Date(`${yr}-${String(p2).padStart(2, "0")}-${String(p1).padStart(2, "0")}`)
      : new Date(`${yr}-${String(p2).padStart(2, "0")}-${String(p1).padStart(2, "0")}`);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  }
  const p = Date.parse(s);
  if (!isNaN(p)) return new Date(p).toISOString().slice(0, 10);
  return null;
}

// FIX: убраны избыточные backslash в character class — /^["']|["']$/g
function stripQuotes(s) {
  return String(s).replace(/^["']|["']$/g, "");
}

function smartParseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) {
    return { items: [], schemaError: "CSV file is empty — need at least 2 rows (header + data)", warnings: [], schema: null };
  }

  // Авто-детект разделителя
  const delim = [",", ";", "\t"].reduce((best, d) => {
    const cnt = (lines[0].match(new RegExp("\\" + (d === "\t" ? "t" : d), "g")) || []).length;
    const bestCnt = (lines[0].match(new RegExp("\\" + (best === "\t" ? "t" : best), "g")) || []).length;
    return cnt > bestCnt ? d : best;
  }, ",");

  const rawH = lines[0].split(delim).map(h => stripQuotes(h.trim()));

  const dateCol    = findCol(rawH, DATE_PATTERNS);
  const spendCol   = findCol(rawH, SPEND_PATTERNS);
  const revenueCol = findCol(rawH, REVENUE_PATTERNS);

  const missing = [];
  if (!dateCol)    missing.push("date (date / day / dt / timestamp / report_date)");
  if (!spendCol)   missing.push("spend (spend / cost / ad_spend / traffic_cost / budget)");
  if (!revenueCol) missing.push("revenue (revenue / rev / income / payout)");

  if (missing.length) {
    return {
      items: [], schemaError: `Required columns not found: ${missing.join("; ")}`,
      warnings: [], schema: null,
    };
  }

  const campaignCol = findCol(rawH, ["campaign", "campaign_name", "campaignname", "offer", "offer_name"]);
  const clicksCol   = findCol(rawH, ["clicks", "click", "visits"]);

  const items = [], warnings = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const vals = line.split(delim).map(v => stripQuotes(v.trim()));
    const row = {};
    rawH.forEach((h, idx) => { row[h] = vals[idx] ?? ""; });

    const dateStr = parseDateStr(row[dateCol]);
    if (!dateStr) {
      warnings.push(`Row ${i}: could not parse date "${row[dateCol]}" (column "${dateCol}")`);
      continue;
    }

    const spend   = parseNum(row[spendCol]);
    const revenue = parseNum(row[revenueCol]);
    const ts      = Math.floor(new Date(dateStr + "T00:00:00Z").getTime() / 1000);

    items.push({
      timestamp:       ts,
      spend:           Math.max(0, spend),
      revenue:         Math.max(0, revenue),
      campaign:        campaignCol ? (String(row[campaignCol] ?? "").trim() || null) : null,
      clicks:          clicksCol   ? (parseInt(row[clicksCol] ?? "0", 10) || 0) : 0,
      conversions:     0,
      currency:        "EUR",
      source_event_id: null,
      raw:             { date: dateStr, spend, revenue },
    });
  }

  return {
    items,
    schemaError: null,
    warnings,
    schema: { dateCol, spendCol, revenueCol, campaignCol },
  };
}

/* ============================================================
   useIngestionStatus — статус батчей и очереди
   ============================================================ */
export function useIngestionStatus() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    workerClient("/api/ingest/status")
      .then(res  => { setData(res); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []); // нет внешних зависимостей

  useEffect(() => { refresh(); }, [refresh]);

  return { data, loading, error, refresh };
}

/* ============================================================
   useIngestionUpload — загрузка и отправка CSV
   ============================================================ */
export function useIngestionUpload() {
  const [preview,   setPreview]   = useState(null);
  const [importing, setImporting] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState(null);
  const [step,      setStep]      = useState("idle");

  const previewFile = useCallback(async (file) => {
    setError(null);
    setPreview(null);
    setStep("idle");

    try {
      const text = await file.text();
      const { items, schemaError, warnings, schema } = smartParseCSV(text);

      // Ошибка схемы — нет нужных колонок
      if (schemaError && items.length === 0) {
        setError(schemaError);
        return;
      }

      if (items.length === 0) {
        setError("CSV is empty or all rows are invalid.");
        return;
      }

      setStep("parsed");

      // Отправляем на backend preview для валидации
      const res = await fetch(`${BASE_URL}/api/ingest/preview`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ source: "csv", items }),
      }).then(r => r.json());

      setPreview({ ...res, items });
      setStep("previewed");
    } catch (err) {
      setError(err.message ?? "File read error");
      setStep("idle");
    }
  }, []); // FIX: убран [parseCSV] из deps — smartParseCSV чистая module-level функция

  const importData = useCallback(async (items, source = "csv") => {
    if (!items?.length) {
      setError("No valid data to import");
      return;
    }
    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const batchId = `${source}_${Date.now()}`;
      const res = await fetch(`${BASE_URL}/api/ingest`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          schema_version: "1.2",
          batch_id:       batchId,
          source,
          items,
        }),
      }).then(r => r.json());

      setResult(res);
      setStep("imported");
    } catch (err) {
      setError(err.message ?? "Import failed");
    } finally {
      setImporting(false);
    }
  }, []); // нет внешних зависимостей

  const reset = useCallback(() => {
    setPreview(null);
    setResult(null);
    setError(null);
    setStep("idle");
  }, []);

  return { preview, importing, result, error, step, previewFile, importData, reset };
}
